import React, { useEffect, useMemo, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { GenericSelect } from '@/styles/GenericForm';
import { useProject, PROJECT_SELECT_WIDTH_CLASS } from '@/components/ProjectSelector';
import { colors } from '@/design-system/colors';
import { formatClock } from '@/lib/format-duration';
import { useTimeTracker } from '../time-tracker-context';

/**
 * The tracker's face. It owns no tracking state: the running session lives
 * in the app-level TimeTrackerProvider (so it survives navigation).
 *
 * Which project the selector shows:
 *   - tracker running  -> the project the tracker is pinned to (whatever the
 *                         Global Project Selector says);
 *   - tracker stopped  -> the Global Project Selector's project, or "Select"
 *                         when that is "All Projects". The user may pick a
 *                         different one here for the next session; that pick is
 *                         local to this selector and never changes the global one,
 *                         and the selector follows the global one again as soon
 *                         as that changes.
 */
export function Timer() {
  const { selectedProject: globalProject, projects } = useProject();
  const { session, isRunning, elapsedSeconds, isSaving, start, stop } = useTimeTracker();
  const [picked, setPicked] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => { setPicked(null); }, [globalProject]);

  const idleProject = picked ?? (globalProject && globalProject !== 'all' ? globalProject : '');
  const shownProject = session ? session.projectId : idleProject;

  // The tracked project may no longer be in the active list; it must still read correctly.
  const options = useMemo(() => {
    const list = projects.map((p) => ({ label: p.name, value: p.id }));
    if (session && !list.some((o) => o.value === session.projectId)) list.unshift({ label: session.projectName, value: session.projectId });
    return list;
  }, [projects, session]);

  const toggleTimer = async () => {
    if (isRunning) {
      try {
        await stop();
        setShowError(false);
      } catch (err) {
        console.error('Error saving timer time entry:', err);
        alert('Failed to save time entry: ' + (err instanceof Error ? err.message : 'Unknown error'));
      }
      return;
    }
    if (!idleProject) {
      setShowError(true);
      return;
    }
    const project = projects.find((p) => p.id === idleProject);
    start(idleProject, project?.name || 'Unknown Project');
    setShowError(false);
  };

  return (
    <Card className="w-80 h-48">
      <CardContent className="p-3 h-full flex flex-col">
        <div className="flex flex-col items-center gap-3">
          <div className="w-full text-center">
            <div className="text-2xl font-mono font-bold text-gray-800 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
              {formatClock(elapsedSeconds)}
            </div>
          </div>

          <Button
            onClick={toggleTimer}
            disabled={isSaving}
            aria-label={isRunning ? 'Stop and save' : 'Start tracking'}
            size="sm"
            className="!rounded-full !w-10 !h-10 !p-0 shadow-lg -mt-6"
            style={{ borderRadius: '50%', backgroundColor: colors.buttonPrimary, color: colors.buttonTextWhite }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.buttonPrimaryHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.buttonPrimary; }}
          >
            {isRunning ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 ml-0.5 fill-current" />}
          </Button>
        </div>

        <div className="mt-1"></div>

        <div className="space-y-1">
          <Label htmlFor="timer-project" className="text-xs text-gray-600">Project:</Label>
          <GenericSelect
            id="timer-project"
            value={shownProject}
            onValueChange={(value) => { setPicked(value); setShowError(false); }}
            disabled={isRunning}
            required
            placeholder="Select"
            className={`${PROJECT_SELECT_WIDTH_CLASS} h-7 text-xs`}
            contentClassName="!overflow-y-auto max-h-[15rem] [&>div]:!h-auto [&>div]:!max-h-[15rem]"
            options={options}
          />
        </div>

        {session ? (
          <div className="text-xs text-center text-green-600 font-medium mt-2">Tracking {session.projectName}</div>
        ) : showError ? (
          <div className="text-xs text-center text-red-600 font-medium mt-2">Select a project before starting the timer</div>
        ) : (
          <div className="text-xs text-center text-gray-500 mt-2">Select a project</div>
        )}
      </CardContent>
    </Card>
  );
}
